import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import { FileIcon, FileTextIcon, ImageIcon, DownloadIcon, XIcon, Loader2Icon, AlertCircleIcon, MusicIcon, PlayIcon, PauseIcon, } from "lucide-react";
import { getFileExtension, getShortFilename } from "@f2e/shared/utils";
import { downloadFile } from "@f2e/shared/browser/download";
import { cn } from "../../lib/utils";
import { Button } from "../../components/base";
import { useUiTranslation } from "../../i18n";
const getFileIcon = (type) => {
    if (type.startsWith("image/")) {
        return ImageIcon;
    }
    if (type === "application/pdf" ||
        type.includes("document") ||
        type === "text/plain" ||
        type === "text/markdown") {
        return FileTextIcon;
    }
    return FileIcon;
};
export const AttachmentCard = ({ file, className, showRemove = false, resolveUrl, onClick, onRemove, onDownload, onPreviewLoad, }) => {
    const { t } = useUiTranslation();
    const contentUnavailable = file.meta?.contentUnavailable === true;
    const [imageError, setImageError] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [isAudioPreviewLoading, setIsAudioPreviewLoading] = useState(false);
    const fileReaderRef = useRef(null);
    const audioRef = useRef(null);
    const audioObjectUrlRef = useRef(null);
    const FileIconComponent = getFileIcon(file.type);
    const isImage = file.type.startsWith("image/") && !contentUnavailable;
    const isAudio = file.type.startsWith("audio/") && !contentUnavailable;
    const isLoading = file.status === "uploading" || file.status === "pending";
    const hasError = file.status === "error";
    const canRemove = showRemove && onRemove && !isLoading;
    const canPlayPersistedAudio = !!file.url && !!onPreviewLoad;
    const canShowAudioPlayer = !!audioPreviewUrl || canPlayPersistedAudio;
    // Resolve URL helper
    const getFullUrl = useCallback((url) => {
        if (!url)
            return "";
        if (resolveUrl)
            return resolveUrl(url);
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        return url;
    }, [resolveUrl]);
    const revokeAudioObjectUrl = () => {
        if (!audioObjectUrlRef.current)
            return;
        window.URL.revokeObjectURL(audioObjectUrlRef.current);
        audioObjectUrlRef.current = null;
    };
    const formatAudioTime = (seconds) => {
        if (seconds === undefined)
            return "--:--";
        if (!Number.isFinite(seconds) || seconds <= 0)
            return "0:00";
        const totalSeconds = Math.floor(seconds);
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };
    // Generate preview for image files during upload with proper cleanup
    useEffect(() => {
        // Clear preview when URL becomes available (upload complete)
        if (file.url && imagePreview) {
            setImagePreview(null);
            return;
        }
        // Generate preview only when needed
        if (!isImage || !file.fileObject || file.url || imagePreview) {
            return;
        }
        let isMounted = true;
        const reader = new FileReader();
        fileReaderRef.current = reader;
        reader.onloadend = () => {
            if (isMounted && reader.result) {
                setImagePreview(reader.result);
            }
        };
        reader.onerror = () => {
            console.error("Failed to read file for preview:", {
                fileName: file.fileObject.name,
                error: reader.error,
            });
        };
        reader.readAsDataURL(file.fileObject);
        return () => {
            isMounted = false;
            if (fileReaderRef.current?.readyState === FileReader.LOADING) {
                fileReaderRef.current.abort();
            }
        };
    }, [isImage, file.fileObject, file.url, imagePreview]);
    useEffect(() => {
        if (!isAudio || !file.fileObject) {
            revokeAudioObjectUrl();
            setAudioPreviewUrl(null);
            return;
        }
        const objectUrl = window.URL.createObjectURL(file.fileObject);
        revokeAudioObjectUrl();
        audioObjectUrlRef.current = objectUrl;
        setAudioPreviewUrl(objectUrl);
        return () => {
            if (audioObjectUrlRef.current === objectUrl) {
                window.URL.revokeObjectURL(objectUrl);
                audioObjectUrlRef.current = null;
            }
        };
    }, [isAudio, file.fileObject, file.id, file.url]);
    useEffect(() => {
        return () => {
            revokeAudioObjectUrl();
        };
    }, []);
    const handleRemove = (e) => {
        e.stopPropagation();
        if (onRemove) {
            onRemove(file.id);
        }
    };
    const handleAudioPlayError = (error) => {
        setIsAudioPlaying(false);
        console.warn("Failed to play audio attachment", {
            fileName: file.name,
            error,
        });
    };
    const playAudioAfterSourceChange = () => {
        requestAnimationFrame(() => {
            audioRef.current?.play().catch(handleAudioPlayError);
        });
    };
    const handleDownload = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Don't handle clicks while uploading
        if (isLoading) {
            return;
        }
        // If custom onClick is provided, use it instead of default download behavior
        if (onClick) {
            onClick();
            return;
        }
        // No URL available - nothing to download
        if (!file.url) {
            console.warn("Download attempted but no URL available", {
                fileId: file.id,
                fileName: file.name,
            });
            return;
        }
        const fullUrl = getFullUrl(file.url);
        let absoluteUrl;
        // Validate URL before attempting download
        try {
            absoluteUrl = new URL(fullUrl, window.location.origin).toString();
        }
        catch {
            console.error("Invalid download URL", {
                url: fullUrl,
                fileName: file.name,
            });
            return;
        }
        // Use custom download handler if provided
        if (onDownload) {
            let success = false;
            try {
                success = await onDownload(file);
            }
            catch (error) {
                console.warn("Custom download failed", {
                    fileName: file.name,
                    error,
                });
            }
            if (!success) {
                // Fallback: open in new tab
                console.warn("Custom download failed, falling back to new tab", {
                    url: absoluteUrl,
                });
                window.open(absoluteUrl, "_blank", "noopener,noreferrer");
            }
            return;
        }
        // Download via fetch using shared utility
        const success = await downloadFile(absoluteUrl, file.name);
        if (!success) {
            // Fallback: open in new tab
            console.warn("Primary download failed, falling back to new tab", {
                url: absoluteUrl,
            });
            window.open(absoluteUrl, "_blank", "noopener,noreferrer");
        }
    };
    const toggleAudioPlayback = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const audio = audioRef.current;
        if (!audio)
            return;
        if (!audio.paused) {
            audio.pause();
            setIsAudioPlaying(false);
            return;
        }
        try {
            if (!audioSrc) {
                if (!canPlayPersistedAudio)
                    return;
                setIsAudioPreviewLoading(true);
                try {
                    if (!onPreviewLoad)
                        return;
                    const previewUrl = await onPreviewLoad(file);
                    revokeAudioObjectUrl();
                    if (previewUrl.startsWith("blob:")) {
                        audioObjectUrlRef.current = previewUrl;
                    }
                    setAudioPreviewUrl(previewUrl);
                }
                catch (error) {
                    console.warn("Failed to load protected audio preview, falling back to direct media URL", {
                        fileName: file.name,
                        error,
                    });
                    revokeAudioObjectUrl();
                    if (!file.url)
                        return;
                    setAudioPreviewUrl(getFullUrl(file.url));
                }
                finally {
                    setIsAudioPreviewLoading(false);
                }
                playAudioAfterSourceChange();
                return;
            }
            await audio.play();
            setIsAudioPlaying(true);
        }
        catch (error) {
            setIsAudioPreviewLoading(false);
            handleAudioPlayError(error);
        }
    };
    const fileExtension = getFileExtension(file.name);
    const shortFilename = getShortFilename(file.name);
    const audioSrc = audioPreviewUrl || "";
    const metaDurationSeconds = typeof file.meta?.durationMs === "number" && file.meta.durationMs > 0
        ? file.meta.durationMs / 1000
        : undefined;
    const audioDisplayTime = audioCurrentTime > 0
        ? audioCurrentTime
        : audioDuration > 0
            ? audioDuration
            : metaDurationSeconds;
    return (_jsxs("div", { className: cn("rounded-lg border border-border/50 bg-card shadow-sm", "w-24 h-24", "transition-all hover:shadow-md hover:border-border", "group flex flex-col overflow-hidden relative", !isAudio &&
            !contentUnavailable &&
            (onClick || file.url) &&
            !isLoading &&
            "cursor-pointer", hasError && "border-destructive/50 bg-destructive/10", isLoading && "opacity-70", className), onClick: isAudio || contentUnavailable ? undefined : handleDownload, "aria-disabled": contentUnavailable || undefined, children: [canRemove && (_jsx(Button, { type: "button", variant: "ghost", size: "icon", className: "absolute top-1 right-1 h-5 w-5 p-0 bg-background/80 hover:bg-background rounded-full z-10", onClick: handleRemove, children: _jsx(XIcon, { className: "h-3 w-3" }) })), isAudio && file.url && !isLoading && !contentUnavailable && (_jsx(Button, { type: "button", variant: "ghost", size: "icon", className: "absolute top-1 left-1 h-5 w-5 p-0 rounded-full z-10 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 bg-foreground/75 text-background hover:bg-foreground/85 hover:text-background dark:bg-background/80 dark:text-foreground dark:hover:bg-background dark:hover:text-foreground", title: `Download ${file.name}`, onClick: handleDownload, children: _jsx(DownloadIcon, { className: "h-3 w-3" }) })), isLoading && (_jsx("div", { className: "absolute inset-0 bg-background/50 flex items-center justify-center z-10 pointer-events-none", children: _jsx(Loader2Icon, { className: "h-5 w-5 animate-spin text-primary" }) })), hasError && (_jsx("div", { className: "absolute top-1 left-1 z-10 pointer-events-none", children: _jsx(AlertCircleIcon, { className: "h-4 w-4 text-destructive" }) })), _jsx("div", { className: cn("w-full h-full", !isAudio && "pointer-events-none"), children: isImage ? (
                // Image attachment - square card with thumbnail
                _jsx("div", { className: "w-full h-full relative group", children: (file.url || imagePreview) && !imageError ? (_jsxs(_Fragment, { children: [_jsx("img", { src: file.url ? getFullUrl(file.url) : imagePreview || "", alt: file.name, className: "w-full h-full object-cover", onError: () => setImageError(true), draggable: false }), !isLoading && (_jsx("div", { className: "absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100", children: _jsx(DownloadIcon, { className: "h-5 w-5 text-white" }) }))] })) : (_jsx("div", { className: "w-full h-full flex items-center justify-center bg-muted/40", children: _jsx(ImageIcon, { className: "h-8 w-8 text-muted-foreground" }) })) })) : isAudio ? (_jsxs("div", { className: "w-full h-full flex flex-col p-2", children: [canShowAudioPlayer ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsxs("div", { className: "relative h-8 w-8 flex items-center justify-center", children: [_jsx(MusicIcon, { className: "h-8 w-8 text-muted-foreground" }), _jsx(Button, { type: "button", variant: "ghost", size: "icon", className: cn("absolute inset-0 m-auto h-8 w-8 rounded-full border border-background/70 bg-foreground/75 text-background shadow-sm hover:bg-foreground/85 hover:text-background transition-opacity dark:border-border/50 dark:bg-background/80 dark:text-foreground dark:hover:bg-muted dark:hover:text-foreground", isAudioPlaying || isAudioPreviewLoading
                                                    ? "opacity-100"
                                                    : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"), title: isAudioPlaying
                                                    ? `Pause ${file.name}`
                                                    : `Play ${file.name}`, "aria-label": isAudioPlaying
                                                    ? `Pause ${file.name}`
                                                    : `Play ${file.name}`, onClick: toggleAudioPlayback, disabled: isAudioPreviewLoading, children: isAudioPreviewLoading ? (_jsx(Loader2Icon, { className: "h-4 w-4 animate-spin" })) : isAudioPlaying ? (_jsx(PauseIcon, { className: "h-4 w-4" })) : (_jsx(PlayIcon, { className: "h-4 w-4 ml-0.5" })) })] }) }), _jsx("audio", { ref: audioRef, className: "hidden", preload: "metadata", src: audioSrc || undefined, onClick: (event) => event.stopPropagation(), onMouseDown: (event) => event.stopPropagation(), onLoadedMetadata: (event) => {
                                        const duration = event.currentTarget.duration;
                                        setAudioDuration(Number.isFinite(duration) && duration > 0 ? duration : 0);
                                    }, onDurationChange: (event) => {
                                        const duration = event.currentTarget.duration;
                                        setAudioDuration(Number.isFinite(duration) && duration > 0 ? duration : 0);
                                    }, onTimeUpdate: (event) => {
                                        setAudioCurrentTime(event.currentTarget.currentTime || 0);
                                    }, onPlay: () => setIsAudioPlaying(true), onPause: () => setIsAudioPlaying(false), onEnded: () => {
                                        setIsAudioPlaying(false);
                                        setAudioCurrentTime(0);
                                    } })] })) : (_jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsx(FileIconComponent, { className: "h-8 w-8 text-muted-foreground" }) })), _jsxs("div", { className: "flex flex-col items-center gap-1 min-w-0", children: [_jsx("span", { className: "text-xs text-foreground text-center truncate w-full px-1", title: file.name, children: shortFilename }), _jsx("span", { className: "text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground font-medium tabular-nums", children: formatAudioTime(audioDisplayTime) })] }), fileExtension && _jsx("span", { className: "sr-only", children: fileExtension })] })) : (
                // Document attachment - square card with filename and extension
                _jsxs("div", { className: "w-full h-full flex flex-col p-2 group relative", children: [_jsx("div", { className: "flex-1 flex items-center justify-center", children: _jsx(FileIconComponent, { className: "h-8 w-8 text-muted-foreground" }) }), _jsxs("div", { className: "flex flex-col items-center gap-1 min-w-0", children: [_jsx("span", { className: "text-xs text-foreground text-center truncate w-full px-1", title: file.name, children: shortFilename }), fileExtension && (_jsx("span", { className: "text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground font-medium", children: fileExtension }))] }), _jsx("div", { className: "absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors opacity-0 group-hover:opacity-100 rounded-lg" })] })) }), file.meta?.sourceStatus === "removed" && (_jsx("span", { className: "absolute bottom-0 inset-x-0 bg-background/95 px-1 py-0.5 text-[10px] text-center text-muted-foreground", children: t(contentUnavailable
                    ? "attachment.sourceRemoved"
                    : "attachment.originalSourceRemoved") }))] }));
};
export const AttachmentCardList = ({ files, className, align = "left", onFileClick, onFileRemove, showRemove = false, resolveUrl, onDownload, onPreviewLoad, }) => {
    if (files.length === 0)
        return null;
    return (_jsx("div", { className: cn("flex flex-wrap gap-2 mb-1", align === "right" && "justify-end", align === "left" && "justify-start", className), children: files.map((file) => (_jsx(AttachmentCard, { file: file, onClick: onFileClick ? () => onFileClick(file) : undefined, onRemove: onFileRemove, showRemove: showRemove, resolveUrl: resolveUrl, onDownload: onDownload, onPreviewLoad: onPreviewLoad }, file.id))) }));
};
//# sourceMappingURL=AttachmentCard.js.map